import Typography from "@mui/material/Typography";
import { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useParams } from "react-router";

import { getArtWarehouseChildObjects, getArtWarehouseObjectById } from "../../api";
import ArtObjectDetailsInfo from "../../components/ArtObjectDetailsInfo/ArtObjectDetailsInfo";
import ArtObjectTabs from "../../components/ArtObjectTabs/ArtObjectTabs";
import { useApi } from "../../context/context";

const ArtObject = () => {
  const { objectId } = useParams();
  const { state } = useLocation();
  const { companyId, user } = useApi();
  const [object, setObject] = useState(
    state?.object?.objectId === objectId ? state.object : null
  );
  const [childObjects, setChildObjects] = useState([]);
  const [parentObjectSerial, setParentObjectSerial] = useState("-");

  useEffect(() => {
    let isMounted = true;

    const loadObject = async () => {
      const data = await getArtWarehouseObjectById({
        companyId,
        token: user?.token,
        objectId
      });

      if (!isMounted) return;

      setObject(
        data || {
          objectId,
          warehouseName: "-",
          orderInDate: "-",
          mainImageId: null,
          imageUrls: [],
          documents: []
        }
      );
    };

    if (state?.object?.objectId === objectId) {
      setObject(state.object);
      return () => {
        isMounted = false;
      };
    }

    loadObject();

    return () => {
      isMounted = false;
    };
  }, [companyId, objectId, state?.object, user?.token]);

  const safeObject = useMemo(
    () =>
      object || {
        objectId,
        warehouseName: "-",
        orderInDate: "-",
        mainImageId: null,
        imageUrls: [],
        documents: []
      },
    [object, objectId]
  );
  useEffect(() => {
    let isMounted = true;

    const loadRelatedObjects = async () => {
      if (!companyId || !user?.token || !safeObject?.objectId) {
        if (!isMounted) return;
        setChildObjects([]);
        setParentObjectSerial("-");
        return;
      }

      if (safeObject.type === "Container") {
        const objects = await getArtWarehouseChildObjects({
          token: user.token,
          parentObjectId: safeObject.objectId
        });

        if (!isMounted) return;

        setChildObjects(objects);
        setParentObjectSerial("-");
        return;
      }

      setChildObjects([]);

      if (!safeObject.parentObjectId) {
        if (!isMounted) return;
        setParentObjectSerial("-");
        return;
      }

      const parentObject = await getArtWarehouseObjectById({
        companyId,
        token: user.token,
        objectId: safeObject.parentObjectId
      });

      if (!isMounted) return;

      setParentObjectSerial(parentObject?.serialNumber || "-");
    };

    loadRelatedObjects();

    return () => {
      isMounted = false;
    };
  }, [
    companyId,
    safeObject.objectId,
    safeObject.parentObjectId,
    safeObject.type,
    user?.token
  ]);

  return (
    <>
      <Link
        className="back-button"
        to={`/art-warehouse${state?.fromSearch || ""}`}
      >
        Back
      </Link>

      <div className="breadcrumb">
        <Link to="/art-warehouse">
          Art Warehouse / Serial {safeObject.serialNumber || "-"}
        </Link>
      </div>

      <Typography variant="h1" color="primary.main">
        {safeObject.type || "Object"}
      </Typography>

      <ArtObjectDetailsInfo
        inventoryNo={safeObject.inventoryNo || "-"}
        warehouseName={safeObject.warehouseName}
        type={safeObject.type}
        orderInDate={safeObject.orderInDate}
        showImage={false}
      />

      <ArtObjectTabs
        object={safeObject}
        documents={safeObject.documents || []}
        imageUrls={safeObject.imageUrls || []}
        childObjects={childObjects}
        parentObjectSerial={parentObjectSerial}
        fromSearch={state?.fromSearch || ""}
      />
    </>
  );
};

export default ArtObject;
